import type { FC, ReactNode } from 'react'
import type { Collection } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { MediaImage } from '@/components/generals/media-image'

interface CollectionTilesProps {
    collections: Array<Collection>
}

interface CollectionTileProps {
    collection: Collection
    large?: boolean
}

const CollectionTile: FC<CollectionTileProps> = ({ collection, large = false }): ReactNode => {
    return (
        <Link href={`/collections/${collection.slug}`} className='group relative block overflow-hidden bg-muted'>
            <div className={cn('relative', large ? 'aspect-[4/5]' : 'aspect-[3/4]')}>
                <MediaImage
                    src={collection.image}
                    alt={collection.imageAlt}
                    sizes={large ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'}
                    className='object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.035]'
                />

                <div aria-hidden className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent' />
            </div>

            <div className='absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-6 text-white lg:p-10'>
                <div>
                    {collection.eyebrow && (
                        <p data-mdlfy-cms={cms('collections', collection.id, 'eyebrow')} className='eyebrow text-white/80'>
                            {collection.eyebrow}
                        </p>
                    )}

                    <h3
                        data-mdlfy-cms={cms('collections', collection.id, 'name')}
                        className={cn('mt-3 font-heading font-light leading-none', large ? 'text-5xl lg:text-7xl' : 'text-4xl lg:text-5xl')}
                    >
                        {collection.name}
                    </h3>
                </div>

                <span className='eyebrow link-underline shrink-0'>
                    Discover
                </span>
            </div>
        </Link>
    )
}

export const CollectionTiles: FC<CollectionTilesProps> = ({ collections }): ReactNode => {
    if (collections.length === 0) return null

    const primary = collections.slice(0, 2)
    const secondary = collections.slice(2)

    return (
        <section id='collections' aria-labelledby='collections-title' className='px-4 pb-24 sm:px-6 lg:px-10 lg:pb-36'>
            <div className='mx-auto max-w-[1600px]'>
                <div className='reveal mb-10 flex flex-col gap-4 px-2 sm:flex-row sm:items-end sm:justify-between lg:mb-14'>
                    <h2 id='collections-title' className='font-heading text-4xl font-light sm:text-5xl lg:text-6xl'>
                        The Collections
                    </h2>

                    <p className='max-w-sm text-sm leading-relaxed text-muted-foreground'>
                        Ready-to-wear, leather goods, jewelry and fragrance, made in our ateliers in France, Italy and Scotland.
                    </p>
                </div>

                <div className='grid gap-4 md:grid-cols-2'>
                    {primary.map(collection => (
                        <CollectionTile key={collection.id} collection={collection} large />
                    ))}
                </div>

                {secondary.length > 0 && (
                    <div className='mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {secondary.map(collection => (
                            <CollectionTile key={collection.id} collection={collection} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}
